import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

// Tailwind sınıflarını birleştirmek için yardımcı fonksiyon
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Koşullu Tailwind sınıfları için yardımcı fonksiyon
export function cva(base: string, variants: Record<string, Record<string, string>>, defaultVariants: Record<string, string> = {}) {
  return (props: Record<string, string> = {}) => {
    const variantClasses = Object.entries(variants).map(([variantName, variantOptions]) => {
      const variantValue = props[variantName] || defaultVariants[variantName]
      return variantValue ? variantOptions[variantValue] : null
    }).filter(Boolean)
    
    return cn(base, ...variantClasses)
  }
}

// Sadece koşullu sınıflar için yardımcı fonksiyon
export function cx(conditions: Record<string, boolean>) {
  return Object.entries(conditions)
    .filter(([_, condition]) => Boolean(condition))
    .map(([className]) => className)
    .join(' ')
}

// Duruma göre (responsive, dark mode, vb.) sınıflar için yardımcı fonksiyon
export function classNames(...classes: (string | boolean | undefined | null)[]) {
  return classes.filter(Boolean).join(' ')
}

// Hover, focus gibi state'leri yönetmek için yardımcı fonksiyon
export function mergeStyles(baseStyle: string, stateStyles: Record<string, string>, activeStates: Record<string, boolean> = {}) {
  const activeClasses = Object.entries(stateStyles)
    .filter(([stateName]) => activeStates[stateName])
    .map(([_, className]) => className)
  
  return cn(baseStyle, ...activeClasses)
}

// Para birimini formatlamak için yardımcı fonksiyon
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount)
}

// Browser eklentilerinden gelen DOM değişikliklerini temizleyen fonksiyon
// Bu fonksiyon, hydration hatalarını önlemek için React bileşenlerinin ilk bağlanma 
// (mount) aşamasında çağrılabilir
export function cleanupBrowserInjections(element: HTMLElement | null) {
  if (typeof window === 'undefined' || !element) return; // SSR'da çalışma
  
  // Bitdefender gibi eklentilerin eklediği özellikleri temizle
  const injectedAttributes = ['bis_skin_checked', 'data-bit'];
  
  const cleanNode = (node: HTMLElement) => {
    // Enjekte edilmiş özellikleri temizle
    injectedAttributes.forEach(attr => {
      if (node.hasAttribute(attr)) {
        node.removeAttribute(attr);
      }
    });
    
    // Alt öğelere de uygula
    Array.from(node.children).forEach(child => {
      if (child instanceof HTMLElement) {
        cleanNode(child);
      }
    });
  };
  
  cleanNode(element);
}

// Özel bir React hook olarak da kullanılabilir
export function useHydrationGuard(ref: React.RefObject<HTMLElement>) {
  if (typeof window !== 'undefined' && ref.current) {
    // Tarayıcı tarafında, bileşen bağlandıktan sonra çalışır
    setTimeout(() => {
      cleanupBrowserInjections(ref.current);
    }, 0);
  }
}
